import { useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import ProjectCard from './ProjectCard';
import CreateProjectPopup from './CreateProjectPopup';
import ClearChatConfirmation from '../ClearChatConfirmation';
import DatabaseCard from '../DatabaseCard';

/**
 * ProjectsGrid - Main view showing all projects in a grid layout
 *
 * Props:
 * - projects: Array of project objects
 * - onSelectProject: (project) => void - called when a project is selected
 * - onCreateProject: (title, description) => Promise - called to create a new project
 * - onDeleteProject: (projectId) => Promise - called to delete a project
 * - onRenameProject: (projectId, title, description) => Promise - called to rename a project
 */
const ProjectsGrid = ({
  projects = [],
  onSelectProject,
  onCreateProject,
  onDeleteProject,
  onRenameProject
}) => {
  const { user } = useAuth();
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [deleteConfirmProject, setDeleteConfirmProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent_activity');
  const [showSortMenu, setShowSortMenu] = useState(false);

  // Multi-select state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Mobile panel state for Database
  const [showMobilePanel, setShowMobilePanel] = useState(false);

  // Role-based visibility for Database card
  // Check custom:membership from Cognito (admin, owner) OR role for local dev (developer)
  const membership = user?.['custom:membership'];
  const role = user?.role;
  const canManageDatabase = ['owner', 'admin'].includes(membership) || role === 'developer';

  // Filter and sort projects
  const filteredProjects = projects
    .filter(p => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return p.title?.toLowerCase().includes(query) ||
             p.description?.toLowerCase().includes(query);
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'recent_activity':
          return (b.last_accessed || b.created_at || '').localeCompare(a.last_accessed || a.created_at || '');
        case 'last_edited':
          return (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || '');
        case 'date_created':
          return (b.created_at || '').localeCompare(a.created_at || '');
        default:
          return 0;
      }
    });

  const sortOptions = [
    { value: 'recent_activity', label: 'Recent activity' },
    { value: 'last_edited', label: 'Last edited' },
    { value: 'date_created', label: 'Date created' }
  ];

  const handleCreateProject = async (title, description) => {
    await onCreateProject(title, description);
    setShowCreatePopup(false);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmProject) {
      await onDeleteProject(deleteConfirmProject.project_id);
      setDeleteConfirmProject(null);
    }
  };

  // Multi-select handlers
  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedProjects(new Set());
  };

  const toggleProjectSelection = (projectId) => {
    const newSelected = new Set(selectedProjects);
    if (newSelected.has(projectId)) {
      newSelected.delete(projectId);
    } else {
      newSelected.add(projectId);
    }
    setSelectedProjects(newSelected);
  };

  const selectAll = () => {
    setSelectedProjects(new Set(filteredProjects.map(p => p.project_id)));
  };

  const deselectAll = () => {
    setSelectedProjects(new Set());
  };

  const handleBulkDelete = async () => {
    setIsDeleting(true);
    try {
      // Delete all selected projects one by one
      for (const projectId of selectedProjects) {
        await onDeleteProject(projectId);
      }
      setSelectedProjects(new Set());
      setSelectMode(false);
    } catch (error) {
      console.error('Bulk delete error:', error);
    } finally {
      setIsDeleting(false);
      setShowBulkDeleteConfirm(false);
    }
  };

  // Get session count for delete confirmation message
  const deleteSessionCount = deleteConfirmProject?.session_count || 0;

  // Calculate total sessions in selected projects
  const selectedSessionCount = Array.from(selectedProjects).reduce((total, projectId) => {
    const project = projects.find(p => p.project_id === projectId);
    return total + (project?.session_count || 0);
  }, 0);

  return (
    <>
      {/* Create Project Popup */}
      <CreateProjectPopup
        isOpen={showCreatePopup}
        onConfirm={handleCreateProject}
        onCancel={() => setShowCreatePopup(false)}
      />

      {/* Single Delete Confirmation */}
      <ClearChatConfirmation
        isOpen={!!deleteConfirmProject}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirmProject(null)}
        title="Delete Project"
        message={`Are you sure you want to delete "${deleteConfirmProject?.title || 'this project'}"?`}
        description={deleteSessionCount > 0
          ? `This will permanently delete the project and all ${deleteSessionCount} chat${deleteSessionCount === 1 ? '' : 's'} inside it. This action cannot be undone.`
          : "This will permanently delete the project. This action cannot be undone."
        }
        confirmLabel={deleteSessionCount > 0
          ? `Yes, delete this project and all ${deleteSessionCount} chat${deleteSessionCount === 1 ? '' : 's'}`
          : "Yes, I want to delete this project"
        }
        buttonText="Delete Project"
      />

      {/* Bulk Delete Confirmation */}
      <ClearChatConfirmation
        isOpen={showBulkDeleteConfirm}
        onConfirm={handleBulkDelete}
        onCancel={() => setShowBulkDeleteConfirm(false)}
        title={`Delete ${selectedProjects.size} Project${selectedProjects.size === 1 ? '' : 's'}`}
        message={`Are you sure you want to delete ${selectedProjects.size} selected project${selectedProjects.size === 1 ? '' : 's'}?`}
        description={selectedSessionCount > 0
          ? `This will permanently delete ${selectedProjects.size} project${selectedProjects.size === 1 ? '' : 's'} and ${selectedSessionCount} chat${selectedSessionCount === 1 ? '' : 's'} inside them. This action cannot be undone.`
          : `This will permanently delete ${selectedProjects.size} project${selectedProjects.size === 1 ? '' : 's'}. This action cannot be undone.`
        }
        confirmLabel={isDeleting ? "Deleting..." : `Yes, delete ${selectedProjects.size} project${selectedProjects.size === 1 ? '' : 's'}`}
        buttonText={isDeleting ? "Deleting..." : "Delete Projects"}
      />

      <div
        className="flex flex-col h-full"
        style={{
          backgroundColor: 'var(--bg-primary)',
          color: 'var(--text-primary)'
        }}
      >
        {/* Header */}
        <div
            className="border-b flex justify-center"
            style={{ borderColor: 'var(--border-color, rgba(255,255,255,0.1))' }}
          >
            <div className={`flex items-center px-4 lg:px-6 py-4 w-full ${canManageDatabase ? 'max-w-[1400px]' : ''}`}>
              {/* Left side - Title (matches main content area) */}
              <div className="flex-1 flex items-center">
                {/* Mobile/tablet portrait: title is centered to avoid overlap with sidebar toggle button */}
                <h1 className="text-xl font-bold text-center lg:text-left lg:ml-12 flex-1 lg:flex-none">Projects</h1>

                {/* Mobile buttons */}
                <div className="flex items-center gap-2 lg:hidden">
                  {/* Mobile: Database info button (only for admin/owner) */}
                  {canManageDatabase && (
                    <button
                      onClick={() => setShowMobilePanel(true)}
                      className="p-1.5 rounded-md transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  )}
                  {/* Mobile: small round + button */}
                  <button
                    onClick={() => setShowCreatePopup(true)}
                    className="w-6 h-6 rounded-full text-white flex items-center justify-center"
                    style={{ backgroundColor: 'var(--accent-color, #3b82f6)' }}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Right side - Desktop button (matches Database card panel width) */}
              {canManageDatabase && (
                <div className="hidden lg:flex w-[450px] pl-6 pr-6">
                  <button
                    onClick={() => setShowCreatePopup(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all"
                    style={{ backgroundColor: 'var(--accent-color, #3b82f6)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.filter = 'brightness(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.filter = 'brightness(1)';
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    New Project
                  </button>
                </div>
              )}

              {/* Desktop button when no Database panel */}
              {!canManageDatabase && (
                <button
                  onClick={() => setShowCreatePopup(true)}
                  className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all"
                  style={{ backgroundColor: 'var(--accent-color, #3b82f6)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.filter = 'brightness(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.filter = 'brightness(1)';
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New Project
                </button>
              )}
            </div>
          </div>

        {/* Content with optional side panel */}
        <div className="flex-1 flex overflow-hidden justify-center">
          {/* Wrapper to group main + panel and center on ultrawide */}
          <div className={`flex w-full ${canManageDatabase ? 'max-w-[1400px] pt-4' : ''}`}>
          {/* Main content area */}
          <div className={`flex-1 overflow-y-auto p-4 lg:p-6 ${canManageDatabase ? 'lg:pl-16' : ''}`}>
          {/* Search and Sort */}
          {projects.length > 0 && (
            <div className="max-w-4xl mx-auto mb-4 lg:mb-6">
              {/* Search Bar */}
              <div
                className="flex items-center gap-3 px-4 py-3 rounded-xl mb-4"
                style={{
                  backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.05))',
                  border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
                }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--text-secondary)' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search projects..."
                  className="flex-1 bg-transparent outline-none text-sm"
                  style={{ color: 'var(--text-primary)' }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="p-1 rounded-md"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Project count/Select + Sort - SAME ROW */}
              <div className="flex items-center justify-between">
                {/* Left side: count+select OR select mode controls */}
                {!selectMode ? (
                  <div className="flex items-center gap-3">
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {projects.length} project{projects.length === 1 ? '' : 's'}
                    </span>
                    <button
                      onClick={toggleSelectMode}
                      className="text-sm font-medium"
                      style={{ color: 'var(--accent-color, #3b82f6)' }}
                    >
                      Select
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    {/* Select all checkbox */}
                    <button
                      onClick={selectedProjects.size === filteredProjects.length ? deselectAll : selectAll}
                      className="w-5 h-5 rounded border-2 flex items-center justify-center transition-colors"
                      style={{
                        borderColor: selectedProjects.size > 0 ? 'var(--accent-color, #3b82f6)' : 'var(--text-secondary)',
                        backgroundColor: selectedProjects.size === filteredProjects.length && selectedProjects.size > 0 ? 'var(--accent-color, #3b82f6)' : 'transparent'
                      }}
                    >
                      {selectedProjects.size === filteredProjects.length && selectedProjects.size > 0 && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {selectedProjects.size > 0 && selectedProjects.size < filteredProjects.length && (
                        <div className="w-2 h-0.5 rounded" style={{ backgroundColor: 'var(--accent-color, #3b82f6)' }} />
                      )}
                    </button>
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{selectedProjects.size} selected</span>
                    {/* Delete button */}
                    <button
                      onClick={() => selectedProjects.size > 0 && setShowBulkDeleteConfirm(true)}
                      disabled={selectedProjects.size === 0}
                      className="p-1.5 rounded-lg transition-colors disabled:opacity-30"
                      style={{ color: selectedProjects.size > 0 ? '#ef4444' : 'var(--text-secondary)' }}
                      title="Delete selected"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                    {/* Close button */}
                    <button
                      onClick={toggleSelectMode}
                      className="p-1.5 rounded-lg transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Right side: Sort Dropdown */}
                <div className="flex items-center gap-2 relative">
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Sort by</span>
                <button
                  onClick={() => setShowSortMenu(!showSortMenu)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors"
                  style={{
                    backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.05))',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
                  }}
                >
                  {sortOptions.find(o => o.value === sortBy)?.label || 'Activity'}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showSortMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowSortMenu(false)} />
                    <div
                      className="absolute right-0 top-full mt-1 py-1 rounded-lg shadow-lg z-50 min-w-[160px]"
                      style={{
                        backgroundColor: 'var(--bg-primary)',
                        border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
                      }}
                    >
                      {sortOptions.map(option => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value);
                            setShowSortMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm flex items-center justify-between transition-colors"
                          style={{ color: 'var(--text-primary)' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          {option.label}
                          {sortBy === option.value && (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                </div>
              </div>
            </div>
          )}

          {projects.length === 0 ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center h-full text-center">
              <svg
                className="w-16 h-16 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ color: 'var(--text-secondary)' }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              <h2 className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                No projects yet
              </h2>
              <p className="text-sm mb-6 max-w-sm" style={{ color: 'var(--text-secondary)' }}>
                Create a project to organize related chats together. Perfect for grouping conversations by topic or goal.
              </p>
              <button
                onClick={() => setShowCreatePopup(true)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-all"
                style={{ backgroundColor: 'var(--accent-color, #3b82f6)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.filter = 'brightness(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.filter = 'brightness(1)';
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Your First Project
              </button>
            </div>
          ) : (
            /* Projects Grid - max 3 columns, centered */
            <div className="max-w-4xl mx-auto">
              {filteredProjects.length === 0 ? (
                <div className="text-center py-12" style={{ color: 'var(--text-secondary)' }}>
                  <p className="text-sm">No projects match "{searchQuery}"</p>
                </div>
              ) : (
                <div className="grid gap-3 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredProjects.map(project => (
                    <div key={project.project_id} className="relative">
                      {/* Checkbox overlay in select mode */}
                      {selectMode && (
                        <button
                          onClick={() => toggleProjectSelection(project.project_id)}
                          className="absolute top-3 right-3 z-10 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors"
                          style={{
                            borderColor: selectedProjects.has(project.project_id) ? 'var(--accent-color, #3b82f6)' : 'var(--text-secondary)',
                            backgroundColor: selectedProjects.has(project.project_id) ? 'var(--accent-color, #3b82f6)' : 'rgba(0,0,0,0.5)'
                          }}
                        >
                          {selectedProjects.has(project.project_id) && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      )}
                      <ProjectCard
                        project={project}
                        onClick={() => selectMode ? toggleProjectSelection(project.project_id) : onSelectProject(project)}
                        onDelete={selectMode ? null : () => setDeleteConfirmProject(project)}
                        onRename={selectMode ? null : (title, description) => onRenameProject(project.project_id, title, description)}
                        dimmed={selectMode && !selectedProjects.has(project.project_id)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          </div>

          {/* Right Panel - Database (hidden on mobile, only for admin/owner) */}
          {canManageDatabase && (
            <div
              className="hidden lg:block w-[450px] pt-10 pb-6 pl-0 pr-6 overflow-y-auto flex-shrink-0"
            >
              <DatabaseCard />
            </div>
          )}
          </div>
        </div>

      </div>

      {/* Mobile slide-in panel for Database */}
      {canManageDatabase && (
        <>
          {/* Backdrop with fade animation */}
          <div
            className={`fixed inset-0 z-40 bg-black/50 lg:hidden transition-opacity duration-300 ${
              showMobilePanel ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={() => setShowMobilePanel(false)}
          />
          {/* Panel with slide animation */}
          <div
            className={`fixed top-0 right-0 h-full w-[85%] max-w-[350px] z-50 lg:hidden overflow-y-auto transition-transform duration-300 ease-out ${
              showMobilePanel ? 'translate-x-0' : 'translate-x-full'
            }`}
            style={{
              paddingTop: 'var(--navbar-height)',
              backgroundColor: 'var(--bg-primary)',
              borderLeft: '1px solid var(--border-color, rgba(255,255,255,0.1))'
            }}
          >
            {/* Panel header */}
            <div
              className="flex items-center justify-between p-4 border-b sticky top-0"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border-color, rgba(255,255,255,0.1))'
              }}
            >
              <h2 className="font-semibold">Reference Database</h2>
              <button
                onClick={() => setShowMobilePanel(false)}
                className="p-1.5 rounded-md"
                style={{ color: 'var(--text-secondary)' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Panel content */}
            <div className="p-4">
              <DatabaseCard />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ProjectsGrid;
